import React, { useRef } from 'react';

import { Building2, Loader2, Lock as LockIcon, Upload } from 'lucide-react';

import {
	useCreateEnterpriseSettingsMutation,
	useGetEnterpriseSettingsQuery,
	useUpdateEnterpriseSettingsMutation,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../providers/AppAuthProvider';
import { useTypedSelector } from '../../store';
import { Typography } from '../common';

import {
	DataRetentionFields,
	FIELD_INPUT_CLASS,
	FIELD_LABEL_CLASS,
	useDataRetention,
} from './DataRetentionSettings';
import { useEnterpriseSettingsForm } from './hooks';
import type { EnterpriseSettingsFormState } from './hooks';

interface EnterpriseSettingsProps {
	onBack?: () => void;
}

const EnterpriseSettings: React.FC<EnterpriseSettingsProps> = ({ onBack }) => {
	const { showToast } = useToast();
	const { isAdmin } = useAuth();
	const { data: settings, isLoading } = useGetEnterpriseSettingsQuery();
	const [createSettings, { isLoading: isCreating }] =
		useCreateEnterpriseSettingsMutation();
	const [updateSettings, { isLoading: isUpdating }] =
		useUpdateEnterpriseSettingsMutation();

	const [state, dispatch] = useEnterpriseSettingsForm(settings);
	const retention = useDataRetention(isAdmin);
	const customDomain = useTypedSelector(
		(root) => root.tenant.tenant?.custom_domain,
	);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				showToast('Invalid File', 'Please select a valid image file', 'error');
				// Reset file input
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return;
			}

			// Validate file size (max 2MB)
			if (file.size > 2 * 1024 * 1024) {
				showToast('File Too Large', 'File size must be less than 2MB', 'error');
				// Reset file input
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return;
			}

			const reader = new FileReader();
			reader.onloadend = () => {
				dispatch({
					type: 'SET_LOGO',
					file,
					preview: reader.result as string,
				});
			};
			reader.readAsDataURL(file);
		}
	};

	const saveSettings = async () => {
		// Create FormData
		const formData = new FormData();
		formData.append('company_name', state.companyName);
		if (state.logoFile) {
			formData.append('logo', state.logoFile);
		}
		if (state.addressStreet)
			formData.append('address_street', state.addressStreet);
		if (state.addressCity) formData.append('address_city', state.addressCity);
		if (state.addressState)
			formData.append('address_state', state.addressState);
		if (state.addressZip) formData.append('address_zip', state.addressZip);
		if (state.addressCountry)
			formData.append('address_country', state.addressCountry);
		if (state.contactPhone)
			formData.append('contact_phone', state.contactPhone);
		if (state.contactEmail)
			formData.append('contact_email', state.contactEmail);
		if (state.contactWebsite)
			formData.append('contact_website', state.contactWebsite);

		if (settings) {
			// Update using PATCH
			await updateSettings(formData).unwrap();
		} else {
			// Create using POST
			await createSettings(formData).unwrap();
		}

		dispatch({ type: 'RESET_CHANGES' });
		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSave = async () => {
		try {
			if (state.hasChanges) await saveSettings();
			if (retention.hasChanges) await retention.save();
			showToast(
				settings ? 'Settings Updated' : 'Settings Created',
				settings
					? 'Organization settings have been updated successfully'
					: 'Organization settings have been created successfully',
				'success',
			);
		} catch (err: any) {
			console.error('Failed to save settings:', err);
			showToast(
				'Save Failed',
				err?.data?.message || 'Failed to save settings. Please try again.',
				'error',
			);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="flex flex-col items-center space-y-4">
					<Loader2 className="h-8 w-8 animate-spin text-primary-600" />
					<Typography variant="body2" color="muted">
						Loading organization settings...
					</Typography>
				</div>
			</div>
		);
	}

	const isSaving = isCreating || isUpdating || retention.isSaving;

	const field = (
		label: string,
		key: keyof EnterpriseSettingsFormState,
		type = 'text',
	) => (
		<div>
			<label htmlFor={`org-${key}`} className={FIELD_LABEL_CLASS}>
				{label}
			</label>
			<input
				id={`org-${key}`}
				type={type}
				className={FIELD_INPUT_CLASS}
				value={state[key] as string}
				onChange={(e) =>
					dispatch({ type: 'SET_FIELD', field: key, value: e.target.value })
				}
				disabled={!isAdmin}
			/>
		</div>
	);

	return (
		<div className="space-y-4">
			<div className="bg-white border border-gray-200 rounded-lg">
				<div className="p-6 flex items-center justify-between gap-3">
					<div className="flex items-center gap-3">
						<span className="w-10 h-10 flex flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-700">
							<Building2 className="h-5 w-5" />
						</span>
						<div>
							<div className="text-xl font-semibold leading-normal text-gray-900">
								Organization Settings
							</div>
							<div className="text-sm text-gray-500 mt-0.5">
								Configure your company branding for PDF reports
							</div>
						</div>
					</div>
					{!isAdmin && (
						<div className="flex items-center space-x-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
							<LockIcon className="h-4 w-4" />
							<Typography variant="caption">Read Only</Typography>
						</div>
					)}
				</div>
			</div>

			<div className="bg-white border border-gray-200 rounded-lg">
				<div className="p-6 space-y-4">
					<div>
						<label className={FIELD_LABEL_CLASS}>Company branding</label>
						<div className="flex items-start gap-6">
							<div className="text-center">
								<div
									className={`w-32 h-32 border-2 border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}
									onClick={(e) => {
										if (!isAdmin) return;
										e.preventDefault();
										fileInputRef.current?.click();
									}}
								>
									{state.logoPreview ? (
										<img
											src={state.logoPreview}
											alt="Logo preview"
											className="h-14 max-w-full object-contain"
										/>
									) : (
										<div className="text-center">
											<Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
											<span className="text-xs text-gray-500 font-medium">
												Upload Logo
											</span>
										</div>
									)}
								</div>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									onChange={handleLogoSelect}
									className="hidden"
								/>
								<div className="text-xs text-gray-500 mt-2">
									PNG/JPEG, max 2MB
								</div>
							</div>
							<div className="flex-1">
								<label htmlFor="org-companyName" className={FIELD_LABEL_CLASS}>
									Company name <span className="text-red-500">*</span>
								</label>
								<input
									id="org-companyName"
									className={FIELD_INPUT_CLASS}
									value={state.companyName}
									onChange={(e) =>
										dispatch({
											type: 'SET_FIELD',
											field: 'companyName',
											value: e.target.value,
										})
									}
									required
									disabled={!isAdmin}
								/>
							</div>
						</div>
					</div>

					<div>
						<div className="text-base font-medium text-gray-900 mb-4">
							Physical address
						</div>
						<div className="space-y-4">
							{field('Street address', 'addressStreet')}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{field('City', 'addressCity')}
								{field('State', 'addressState')}
								{field('ZIP code', 'addressZip')}
								{field('Country', 'addressCountry')}
							</div>
						</div>
					</div>

					<div>
						<div className="text-base font-medium text-gray-900 mb-4">
							Contact information
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{field('Phone number', 'contactPhone', 'tel')}
							{field('Email', 'contactEmail', 'email')}
							{field('Website', 'contactWebsite', 'url')}
							<div>
								<label htmlFor="org-customDomain" className={FIELD_LABEL_CLASS}>
									Custom domain
								</label>
								{/* Set with the tenant's DNS by a platform admin, so read-only here. */}
								<input
									id="org-customDomain"
									className={FIELD_INPUT_CLASS}
									value={customDomain ?? ''}
									placeholder="—"
									readOnly
								/>
							</div>
						</div>
					</div>

					{retention.available && (
						<DataRetentionFields
							draft={retention.draft}
							onChange={retention.setDraft}
							disabled={!isAdmin}
						/>
					)}

					{isAdmin && (
						<div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
							<button
								type="button"
								className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium leading-5 rounded-lg border border-transparent bg-primary-500 text-white whitespace-nowrap transition-colors hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
								onClick={handleSave}
								disabled={
									!(state.hasChanges || retention.hasChanges) ||
									!state.companyName.trim() ||
									isSaving
								}
							>
								{isSaving ? 'Saving...' : 'Save Changes'}
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EnterpriseSettings;
